import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  ArrowLeft, 
  Clock, 
  Calendar, 
  Award, 
  FileText,
  Upload,
  CheckCircle,
  AlertTriangle,
  Send,
  Loader2,
  File,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  not_started: { label: 'Not Started', color: 'bg-gray-100 text-gray-700', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: FileText },
  submitted: { label: 'Submitted', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  late: { label: 'Late Submission', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
  graded: { label: 'Graded', color: 'bg-purple-100 text-purple-700', icon: Award },
  returned: { label: 'Returned for Revision', color: 'bg-red-100 text-red-700', icon: AlertTriangle }
};

export default function AssignmentDetail() {
  const { assignmentId } = useParams();
  const { token } = useAuth();
  
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [textResponse, setTextResponse] = useState('');
  const [fileUrls, setFileUrls] = useState([]);

  useEffect(() => {
    const fetchAssignment = async () => {
      try {
        const response = await fetch(`${API_URL}/api/portal/assignments/${assignmentId}`, {
          credentials: 'include',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        if (response.ok) {
          const data = await response.json();
          setAssignment(data);
          // Pre-fill with existing submission if any
          if (data.submission?.text_response) {
            setTextResponse(data.submission.text_response);
          }
          if (data.submission?.file_urls) {
            setFileUrls(data.submission.file_urls);
          }
        }
      } catch (err) {
        console.error('Failed to fetch assignment:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAssignment();
  }, [assignmentId, token]);

  const handleSubmit = async () => {
    if (!textResponse && fileUrls.length === 0) {
      toast.error('Please provide a response or upload a file');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/portal/assignments/${assignmentId}/submit`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          text_response: textResponse || null,
          file_urls: fileUrls
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.is_late ? 'Assignment submitted (late)' : 'Assignment submitted successfully!');
        
        // Refresh assignment data
        const refreshResponse = await fetch(`${API_URL}/api/portal/assignments/${assignmentId}`, {
          credentials: 'include',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (refreshResponse.ok) {
          setAssignment(await refreshResponse.json());
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit assignment');
      }
    } catch (err) {
      toast.error('Failed to submit assignment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No due date';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const isOverdue = () => {
    if (!assignment?.due_date) return false;
    return new Date(assignment.due_date) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A] mx-auto mb-4" />
          <p className="text-gray-500">Loading assignment...</p>
        </div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Assignment Not Found</h2>
        <Link to="/portal/assignments">
          <Button variant="outline">Back to Assignments</Button>
        </Link>
      </div>
    );
  }

  const config = statusConfig[assignment.status] || statusConfig.not_started;
  const StatusIcon = config.icon;
  const canSubmit = !['graded'].includes(assignment.status);
  const hasSubmission = assignment.submission?.submission_id;

  return (
    <div className="space-y-6" data-testid="assignment-detail-page">
      {/* Back Button */}
      <Link to="/portal/assignments" className="inline-flex items-center text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Assignments
      </Link>

      {/* Assignment Header */}
      <div className="bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] rounded-2xl p-6 lg:p-8 text-white">
        <div className="flex items-center gap-2 mb-3">
          <Badge className="bg-white/20 text-white border-0">{assignment.course_code}</Badge>
          <Badge className={`${config.color} border-0`}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {config.label}
          </Badge>
        </div>
        
        <h1 className="text-2xl lg:text-3xl font-bold mb-2">{assignment.title}</h1>
        <p className="text-blue-100 mb-4">{assignment.course_title}</p>
        
        <div className="flex flex-wrap gap-6 text-sm">
          <div className={`flex items-center gap-2 ${isOverdue() && !hasSubmission ? 'text-orange-300 font-semibold' : 'text-blue-100'}`}>
            <Calendar className="w-4 h-4" />
            {isOverdue() && !hasSubmission ? 'Overdue: ' : 'Due: '}
            {formatDate(assignment.due_date)}
          </div>
          <div className="flex items-center gap-2 text-blue-100">
            <Award className="w-4 h-4" />
            {assignment.points_possible} points
          </div>
          <div className="flex items-center gap-2 text-blue-100">
            <FileText className="w-4 h-4" />
            {assignment.submission_type === 'text' ? 'Text response' : 
             assignment.submission_type === 'file' ? 'File upload' : 'Text or file'}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Instructions */}
          <Card>
            <CardHeader>
              <CardTitle>Instructions</CardTitle>
            </CardHeader>
            <CardContent>
              <article className="prose prose-slate max-w-none prose-headings:text-gray-900 prose-h2:text-lg prose-h3:text-base prose-p:text-gray-700">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {assignment.instructions || assignment.description}
                </ReactMarkdown>
              </article>
            </CardContent>
          </Card>

          {/* Rubric */}
          {assignment.rubric && assignment.rubric.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Grading Rubric</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {assignment.rubric.map((criterion, idx) => (
                    <div key={idx} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-900">{criterion.name}</h4>
                        <span className="text-sm text-gray-500">{criterion.points} pts</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{criterion.description}</p>
                      {criterion.levels && criterion.levels.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                          {criterion.levels.map((level, lidx) => (
                            <div key={lidx} className="text-xs bg-gray-50 rounded p-2">
                              <div className="font-medium text-gray-900">{level.name}</div>
                              <div className="text-gray-500">{level.points} pts</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Submission Area */}
          {canSubmit && (
            <Card>
              <CardHeader>
                <CardTitle>
                  {hasSubmission ? 'Update Submission' : 'Your Submission'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Text Response */}
                {['text', 'both'].includes(assignment.submission_type) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Text Response
                    </label>
                    <Textarea
                      value={textResponse}
                      onChange={(e) => setTextResponse(e.target.value)}
                      placeholder="Enter your response here..."
                      className="min-h-[200px]"
                    />
                  </div>
                )}

                {/* File Upload */}
                {['file', 'both'].includes(assignment.submission_type) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      File Upload
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 mb-2">
                        Allowed types: {assignment.allowed_file_types?.join(', ')}
                      </p>
                      <p className="text-xs text-gray-400">
                        Max size: {assignment.max_file_size_mb}MB
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        (File upload integration coming soon - use text response for now)
                      </p>
                    </div>
                    
                    {/* Show uploaded files */}
                    {fileUrls.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {fileUrls.map((url, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded p-2">
                            <File className="w-4 h-4 text-gray-500" />
                            <span className="flex-1 text-sm truncate">{url}</span>
                            <button 
                              onClick={() => setFileUrls(fileUrls.filter((_, i) => i !== idx))}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-gray-500">
                    {isOverdue() && assignment.allow_late && (
                      <span className="text-orange-600">
                        Late penalty: {assignment.late_penalty_percent}% will be applied
                      </span>
                    )}
                  </div>
                  <Button 
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {hasSubmission ? 'Resubmit' : 'Submit Assignment'}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Grade Card */}
          {assignment.grade && (
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-purple-900">Your Grade</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center mb-4">
                  <div className="text-4xl font-bold text-purple-700">
                    {assignment.grade.score}/{assignment.points_possible}
                  </div>
                  <div className="text-lg text-purple-600">
                    {assignment.grade.percentage}% • {assignment.grade.letter_grade}
                  </div>
                </div>
                
                {assignment.grade.feedback && (
                  <div className="border-t border-purple-200 pt-4">
                    <h4 className="font-medium text-purple-900 mb-2">Instructor Feedback</h4>
                    <div className="prose prose-sm prose-purple">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {assignment.grade.feedback}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
                
                {assignment.grade.rubric_scores && (
                  <div className="border-t border-purple-200 pt-4 mt-4">
                    <h4 className="font-medium text-purple-900 mb-2">Rubric Scores</h4>
                    <div className="space-y-2">
                      {Object.entries(assignment.grade.rubric_scores).map(([criterion, score]) => (
                        <div key={criterion} className="flex justify-between text-sm">
                          <span className="text-purple-700">{criterion}</span>
                          <span className="font-medium text-purple-900">{score} pts</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Submission Status */}
          {hasSubmission && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle>Submission Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Status</span>
                    <Badge className={config.color}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {config.label}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Submitted</span>
                    <span className="text-sm font-medium">
                      {new Date(assignment.submission.submitted_at).toLocaleDateString()}
                    </span>
                  </div>
                  {assignment.submission.is_late && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Late</span>
                      <Badge className="bg-orange-100 text-orange-700">Yes</Badge>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Version</span>
                    <span className="text-sm font-medium">#{assignment.submission.version}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Submission History */}
          {assignment.submission_history && assignment.submission_history.length > 1 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle>Submission History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {assignment.submission_history.map((sub, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm py-2 border-b last:border-0">
                      <span className="text-gray-500">Version {sub.version}</span>
                      <span className="text-gray-700">
                        {new Date(sub.submitted_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Info */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Assignment Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Course</span>
                <span className="text-sm font-medium">{assignment.course_code}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Points</span>
                <span className="text-sm font-medium">{assignment.points_possible}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Type</span>
                <span className="text-sm font-medium capitalize">{assignment.submission_type}</span>
              </div>
              {assignment.allow_late && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Late Penalty</span>
                  <span className="text-sm font-medium">{assignment.late_penalty_percent}%</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
